/**
 * The global publishing kill switch (M2.1, GET/PUT /api/settings/publishing): a banner on Publish + Home while paused, and the
 * Settings → Publishing card. Resuming while post-restore "already uploaded?" checks are pending returns 409 (dedupe_pending);
 * the owner is told why and may resume anyway (force: true).
 */
import { useState } from "react";
import { ApiError } from "../../api/errors";
import type { PublishingSettings } from "../../api/types";
import { Icon } from "../../components/Icon";
import { useToast } from "../../components/toast";
import { Button, Card, ConfirmDialog, Field, QueryView, Switch } from "../../components/ui";
import { useApi, useCache, useMutation } from "../../data/hooks";
import { usePublishingSettings } from "../../data/queries";
import { dateTime, relative } from "../../lib/format";

const KEYS = ["settings/publishing", "publications", "dashboard"];
/** The API records the actor as "user:<uuid>" (the owner) or a system name; show a person-readable label. */
export const actorLabel = (a: string | null | undefined) => (!a ? null : a.startsWith("user:") ? "you" : a);

function pendingOf(e: unknown): number | null {
  if (!(e instanceof ApiError) || e.code !== "conflict") return null;
  const d = e.details as { reason?: string; pending?: number } | undefined;
  return d?.reason === "dedupe_pending" ? d.pending ?? 0 : null;
}

/** Pause / resume with the 409 → "resume anyway" path. */
export function usePublishingSwitch() {
  const api = useApi();
  const cache = useCache();
  const toast = useToast();
  const [blocked, setBlocked] = useState<number | null>(null);
  const put = useMutation((req: { paused: boolean; reason?: string; force?: boolean }) => api.updatePublishingSettings(req), {
    invalidate: KEYS,
    onSuccess: (s) => { cache.setData("settings/publishing", s); setBlocked(null); toast.ok(s.paused ? "Publishing paused. Nothing uploads until you resume." : "Publishing resumed."); },
    onError: (e) => { const n = pendingOf(e); if (n !== null) setBlocked(n); else toast.error(e, "Couldn't change publishing"); },
  });
  const confirm = blocked !== null && (
    <ConfirmDialog title="Resume anyway?" confirmLabel="Resume anyway" tone="danger" busy={put.pending}
      impact={<>{blocked || "Some"} post{blocked === 1 ? " is" : "s are"} still being checked after the restore, to make sure {blocked === 1 ? "it isn't" : "they aren't"} already on YouTube. Resuming now could upload a duplicate. It's safer to wait a few minutes — the check runs on its own.</>}
      onCancel={() => setBlocked(null)} onConfirm={() => void put.mutate({ paused: false, force: true })} />
  );
  return { put, confirm };
}

export function PauseBanner() {
  const q = usePublishingSettings();
  const { put, confirm } = usePublishingSwitch();
  const s = q.data;
  if (!s?.paused) return null;
  return (
    <div className="pause-banner" role="alert">
      <Icon name="pause" />
      <div className="pause-banner-body">
        <b>Publishing is paused.</b>
        <span className="small">{[s.reason, s.since ? `since ${dateTime(s.since)}` : null, actorLabel(s.updated_by) ? `by ${actorLabel(s.updated_by)}` : null].filter(Boolean).join(" · ")}</span>
        <span className="muted small">Nothing uploads or goes public from ContentOps until you resume. Posts already scheduled on YouTube still go live there.</span>
      </div>
      <Button variant="primary" icon="play" busy={put.pending} onClick={() => void put.mutate({ paused: false })}>Resume</Button>
      {confirm}
    </div>
  );
}

/** Settings → Publishing. */
export function PublishingSettingsCard() {
  const q = usePublishingSettings();
  return <Card title="Publishing" id="publishing"><QueryView q={q}>{(s) => <PublishingSettingsBody s={s} />}</QueryView></Card>;
}

function PublishingSettingsBody({ s }: { s: PublishingSettings }) {
  const { put, confirm } = usePublishingSwitch();
  const [asking, setAsking] = useState(false);
  const [reason, setReason] = useState("");
  return (
    <div className="stack">
      <Switch label="Pause all publishing" checked={s.paused || asking} disabled={put.pending}
        onChange={(v) => { if (v) setAsking(true); else if (asking) setAsking(false); else void put.mutate({ paused: false }); }}
        hint="A kill switch: nothing uploads or goes public from ContentOps while it's on. Posts already scheduled on YouTube still go live there — cancel those individually." />
      {asking && !s.paused && (
        <form className="stack" onSubmit={(e) => { e.preventDefault(); void put.mutateAsync({ paused: true, reason: reason.trim() || undefined }).then(() => { setAsking(false); setReason(""); }, () => undefined); }}>
          <Field label="Why?" optional hint="Shown on the banner, so you remember later.">
            {(id, d) => <input id={id} aria-describedby={d} value={reason} maxLength={500} onChange={(e) => setReason(e.target.value)} placeholder="e.g. Travelling — review the queue first" autoFocus />}
          </Field>
          <div className="row"><Button variant="ghost" onClick={() => setAsking(false)}>Cancel</Button><Button type="submit" variant="danger" busy={put.pending}>Pause publishing</Button></div>
        </form>
      )}
      {s.paused && <p className="note" role="status">Paused{s.reason ? `: ${s.reason}` : ""}{s.since ? ` · ${relative(s.since)}` : ""}{actorLabel(s.updated_by) ? ` · by ${actorLabel(s.updated_by)}` : ""}.</p>}
      {s.pending_dedupe_checks > 0 && <p className="audit-alert small" role="status"><Icon name="clock" size={18} /> {s.pending_dedupe_checks} post{s.pending_dedupe_checks === 1 ? " is" : "s are"} being checked after a restore (not already on YouTube?). Resuming waits for this.</p>}
      {confirm}
    </div>
  );
}
